import { useEffect, useState } from 'react';

interface AddressMapPreviewProps {
  line1: string;
  city: string;
  postalCode: string;
}

/**
 * A live map preview of the address as it's typed.
 *
 * Uses Google's keyless embed (`/maps?...&output=embed`) rather than the Maps
 * JavaScript API — no API key, no new dependency, matching CLAUDE.md's
 * "prefer the simpler solution." This is a staff console screen, not the
 * public listings site, so there's no indexability/SSR concern weighing
 * against a plain iframe.
 *
 * Debounced so the frame reloads a moment after typing stops, not on every
 * keystroke — a keystroke-driven reload would flash blank/reflow constantly.
 */
export function AddressMapPreview({ line1, city, postalCode }: AddressMapPreviewProps) {
  const query = [line1.trim(), city.trim(), 'MI', postalCode.trim()].filter(Boolean).join(', ');
  const ready = line1.trim() !== '' && city.trim() !== '';

  const [debouncedQuery, setDebouncedQuery] = useState(query);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 600);
    return () => clearTimeout(timer);
  }, [query]);

  return (
    <div className="card stack">
      <p className="form-section-label">Map preview</p>
      {ready ? (
        <iframe
          className="map-preview__frame"
          title="Address map preview"
          src={`https://maps.google.com/maps?q=${encodeURIComponent(debouncedQuery)}&output=embed`}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      ) : (
        <p className="muted">Enter a street address and city to preview the map.</p>
      )}
    </div>
  );
}
